import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import * as Random from 'expo-random';

import {
  RECOVERY_DIAGNOSTICS_PUBLIC_KEY,
  RECOVERY_DIAGNOSTICS_PUBLIC_KEY_ID,
} from '@/config/recoveryDiagnostic';
import {
  RECOVERY_SNAPSHOT_TABLE_NAMES,
  type PreparedRecoveryEscrow,
  type RecoveryCaptureAdapter,
  type RecoveryCaptureInput,
  type RecoveryCaptureStore,
  type RecoveryConsistentSnapshot,
  type RecoveryEscrowManifest,
  type RecoveryEscrowPart,
  type RecoveryIdentity,
  type RecoveryMediaDescriptor,
  type RecoveryMediaReader,
  type RecoveryPersistedObject,
  type RecoveryRandomSource,
  type RecoverySnapshotProvider,
  type RecoverySnapshotTableName,
} from '@/types/recovery';
import {
  decryptRecoveryJson,
  encryptRecoveryJson,
  type EncryptedRecoveryExport,
} from './checklistRecoveryCrypto';

const PART_SCHEMA = 'app-guincho-recovery-part/v1' as const;
const MANIFEST_CONTENT_TYPE = 'application/vnd.app-guincho.recovery-manifest+json';
const PART_CONTENT_TYPE = 'application/vnd.app-guincho.recovery-part+json';
const MIN_MAX_PART_BYTES = 2 * 1024;

export interface RecoveryPlaintextPartEnvelope {
  schema: typeof PART_SCHEMA;
  recoveryId: string;
  identity: RecoveryIdentity;
  partId: string;
  index: number;
  kind: 'database' | 'media';
  mediaId: string | null;
  chunkIndex: number;
  chunkCount: number;
  plaintextBytes: number;
  plaintextSha512: string;
  payloadBase64: string;
}

export interface MultipartRecoveryCaptureDependencies {
  snapshotProvider: RecoverySnapshotProvider;
  mediaReader: RecoveryMediaReader;
  store: RecoveryCaptureStore;
  random: RecoveryRandomSource;
  recipientPublicKeyBase64: string;
  recipientKeyId: string;
}

type AppRecoveryCaptureDependencies = Omit<
  MultipartRecoveryCaptureDependencies,
  'random' | 'recipientPublicKeyBase64' | 'recipientKeyId'
>;

interface PlainSource {
  kind: 'database' | 'media';
  mediaId: string | null;
  bytes: Uint8Array;
}

interface BuiltPart {
  descriptor: RecoveryEscrowPart;
  persisted: RecoveryPersistedObject;
}

const cloneBytes = (value: Uint8Array): Uint8Array => new Uint8Array(value);

const sha512 = (value: Uint8Array): string =>
  naclUtil.encodeBase64(nacl.hash(value));

const encodeJson = (value: unknown): Uint8Array =>
  naclUtil.decodeUTF8(stableStringify(value));

const decodeJson = <T>(bytes: Uint8Array): T =>
  JSON.parse(naclUtil.encodeUTF8(bytes)) as T;

const stableStringify = (value: unknown): string => {
  const normalize = (current: unknown): unknown => {
    if (Array.isArray(current)) return current.map(normalize);
    if (current && typeof current === 'object') {
      return Object.keys(current as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((result, key) => {
          const item = (current as Record<string, unknown>)[key];
          if (item !== undefined) result[key] = normalize(item);
          return result;
        }, {});
    }
    return current;
  };
  return JSON.stringify(normalize(value));
};

const assertSafeSegment = (value: string, label: string): void => {
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(value)) {
    throw new Error(`${label} invalido para o escrow.`);
  }
};

const assertIdentity = (identity: RecoveryIdentity): void => {
  assertSafeSegment(identity.companyId, 'Empresa');
  assertSafeSegment(identity.userId, 'Usuario');
  assertSafeSegment(identity.deviceId, 'Dispositivo');
};

const assertSnapshot = (snapshot: RecoveryConsistentSnapshot): void => {
  for (const table of RECOVERY_SNAPSHOT_TABLE_NAMES) {
    if (!Array.isArray(snapshot.tables[table])) {
      throw new Error(`Snapshot incompleto: ${table}.`);
    }
  }
  const mediaIds = new Set<string>();
  for (const media of snapshot.media) {
    assertSafeSegment(media.mediaId, 'Midia');
    if (mediaIds.has(media.mediaId)) throw new Error('Midia duplicada no snapshot.');
    if (!media.sourceUri || !media.contentType) {
      throw new Error(`Referencia de midia invalida: ${media.mediaId}.`);
    }
    mediaIds.add(media.mediaId);
  }
};

const canonicalTables = (
  snapshot: RecoveryConsistentSnapshot
): Record<RecoverySnapshotTableName, readonly unknown[]> =>
  RECOVERY_SNAPSHOT_TABLE_NAMES.reduce((result, table) => {
    result[table] = [...snapshot.tables[table]].sort((left, right) => {
      const leftValue = stableStringify(left);
      const rightValue = stableStringify(right);
      return leftValue < rightValue ? -1 : leftValue > rightValue ? 1 : 0;
    });
    return result;
  }, {} as Record<RecoverySnapshotTableName, readonly unknown[]>);

const splitBytes = (bytes: Uint8Array, chunkSize: number): Uint8Array[] => {
  if (bytes.length === 0) return [new Uint8Array()];
  const chunks: Uint8Array[] = [];
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    chunks.push(bytes.slice(offset, Math.min(offset + chunkSize, bytes.length)));
  }
  return chunks;
};

const objectPrefix = (identity: RecoveryIdentity, recoveryId: string): string =>
  `${identity.companyId}/${identity.userId}/${identity.deviceId}/${recoveryId}`;

const localPartUri = (
  identity: RecoveryIdentity,
  recoveryId: string,
  relativePath: string
): string =>
  `recovery-capture://${identity.companyId}/${identity.userId}/${identity.deviceId}/${recoveryId}/${relativePath}`;

const assertPersistedObject = async (
  store: RecoveryCaptureStore,
  descriptor: { localUri: string; ciphertextBytes: number; ciphertextSha512: string }
): Promise<void> => {
  const bytes = await store.readPersistedObject(descriptor.localUri);
  if (!bytes) throw new Error(`Objeto local do escrow ausente: ${descriptor.localUri}.`);
  if (bytes.length !== descriptor.ciphertextBytes || sha512(bytes) !== descriptor.ciphertextSha512) {
    throw new Error(`Objeto local do escrow corrompido: ${descriptor.localUri}.`);
  }
};

export class MultipartRecoveryCaptureAdapter implements RecoveryCaptureAdapter {
  private readonly snapshotProvider: RecoverySnapshotProvider;
  private readonly mediaReader: RecoveryMediaReader;
  private readonly store: RecoveryCaptureStore;
  private readonly random: RecoveryRandomSource;
  private readonly recipientPublicKey: Uint8Array;
  private readonly recipientKeyId: string;

  constructor(dependencies: MultipartRecoveryCaptureDependencies) {
    this.snapshotProvider = dependencies.snapshotProvider;
    this.mediaReader = dependencies.mediaReader;
    this.store = dependencies.store;
    this.random = dependencies.random;
    this.recipientPublicKey = naclUtil.decodeBase64(dependencies.recipientPublicKeyBase64);
    this.recipientKeyId = dependencies.recipientKeyId;
    if (this.recipientPublicKey.length !== nacl.box.publicKeyLength) {
      throw new Error('Chave publica de recuperacao invalida.');
    }
    if (!this.recipientKeyId.trim()) {
      throw new Error('Identificador da chave de recuperacao ausente.');
    }
  }

  async prepare(input: RecoveryCaptureInput): Promise<PreparedRecoveryEscrow> {
    assertIdentity(input.identity);
    assertSafeSegment(input.recoveryId, 'Recuperacao');
    if (input.maxPartBytes < MIN_MAX_PART_BYTES) {
      throw new Error('Limite multipart insuficiente para criptografia.');
    }

    const persisted = await this.store.loadPrepared(input.identity, input.recoveryId);
    if (persisted) {
      await this.assertPreparedObjects(persisted, input);
      return persisted;
    }

    const snapshot = await this.snapshotProvider.captureConsistentSnapshot(input);
    assertSnapshot(snapshot);
    const tables = canonicalTables(snapshot);
    const databaseBytes = encodeJson({
      schema: 'app-guincho-recovery-database/v1',
      recoveryId: input.recoveryId,
      identity: input.identity,
      createdAt: input.createdAt,
      tables,
    });

    const mediaDescriptors: RecoveryMediaDescriptor[] = [];
    const sources: PlainSource[] = [{ kind: 'database', mediaId: null, bytes: databaseBytes }];
    for (const media of [...snapshot.media].sort((a, b) => a.mediaId.localeCompare(b.mediaId))) {
      const bytes = cloneBytes(await this.mediaReader.readLocalBytes(media));
      if (bytes.length === 0) throw new Error(`Midia local vazia: ${media.mediaId}.`);
      mediaDescriptors.push({
        mediaId: media.mediaId,
        checklistId: media.checklistId,
        fieldId: media.fieldId,
        sourceUri: `local-media://${media.mediaId}`,
        byteLength: bytes.length,
        plaintextSha512: sha512(bytes),
        contentType: media.contentType,
      });
      sources.push({ kind: 'media', mediaId: media.mediaId, bytes });
    }

    const builtParts = await this.buildParts(input, sources);
    const parts = builtParts.map(item => item.descriptor);
    const tableCounts = RECOVERY_SNAPSHOT_TABLE_NAMES.reduce<Record<string, number>>(
      (counts, table) => {
        counts[table] = tables[table].length;
        return counts;
      },
      {}
    );
    const logicalSha512 = sha512(encodeJson({
      databaseSha512: sha512(databaseBytes),
      media: mediaDescriptors.map(media => ({
        mediaId: media.mediaId,
        checklistId: media.checklistId,
        fieldId: media.fieldId,
        contentType: media.contentType,
        byteLength: media.byteLength,
        plaintextSha512: media.plaintextSha512,
      })),
    }));
    const manifest: RecoveryEscrowManifest = {
      schema: 'app-guincho-recovery-escrow/v1',
      recoveryId: input.recoveryId,
      identity: input.identity,
      generatedAt: input.createdAt,
      logicalSha512,
      partCount: parts.length,
      totalCiphertextBytes: parts.reduce((total, part) => total + part.ciphertextBytes, 0),
      tableCounts,
      pendingItemCount: tables.pending_operations.length + tables.pending_deletions.length,
      media: mediaDescriptors,
      parts,
    };
    const manifestEncrypted = await this.encryptValue(manifest);
    const manifestBytes = encodeJson(manifestEncrypted);
    const manifestRelativePath = 'manifest.agmanifest';
    const prefix = objectPrefix(input.identity, input.recoveryId);
    const prepared: PreparedRecoveryEscrow = {
      manifest,
      manifestObject: {
        objectPath: `${prefix}/${manifestRelativePath}`,
        localUri: localPartUri(input.identity, input.recoveryId, manifestRelativePath),
        ciphertextBytes: manifestBytes.length,
        ciphertextSha512: sha512(manifestBytes),
        contentType: MANIFEST_CONTENT_TYPE,
      },
    };
    await this.store.commitPreparedAtomically({
      identity: input.identity,
      recoveryId: input.recoveryId,
      prepared,
      objects: [
        ...builtParts.map(item => item.persisted),
        { relativePath: manifestRelativePath, bytes: manifestBytes },
      ],
    });

    const committed = await this.store.loadPrepared(input.identity, input.recoveryId);
    if (!committed) throw new Error('Commit local do escrow nao confirmado.');
    await this.assertPreparedObjects(committed, input);
    return committed;
  }

  private async buildParts(
    input: RecoveryCaptureInput,
    sources: readonly PlainSource[]
  ): Promise<BuiltPart[]> {
    let rawChunkBytes = Math.max(1, Math.floor((input.maxPartBytes - 1024) * 0.5));
    while (rawChunkBytes >= 1) {
      const result: BuiltPart[] = [];
      let globalIndex = 0;
      let oversized = false;
      for (const source of sources) {
        const chunks = splitBytes(source.bytes, rawChunkBytes);
        for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
          const chunk = chunks[chunkIndex];
          const partId = `part-${String(globalIndex).padStart(6, '0')}`;
          const envelope: RecoveryPlaintextPartEnvelope = {
            schema: PART_SCHEMA,
            recoveryId: input.recoveryId,
            identity: input.identity,
            partId,
            index: globalIndex,
            kind: source.kind,
            mediaId: source.mediaId,
            chunkIndex,
            chunkCount: chunks.length,
            plaintextBytes: chunk.length,
            plaintextSha512: sha512(chunk),
            payloadBase64: naclUtil.encodeBase64(chunk),
          };
          const encrypted = await this.encryptValue(envelope);
          const encryptedBytes = encodeJson(encrypted);
          if (encryptedBytes.length > input.maxPartBytes) {
            oversized = true;
            break;
          }
          const relativePath = `parts/${String(globalIndex).padStart(6, '0')}.agpart`;
          result.push({
            descriptor: {
              partId,
              index: globalIndex,
              kind: source.kind,
              objectPath: `${objectPrefix(input.identity, input.recoveryId)}/${relativePath}`,
              localUri: localPartUri(input.identity, input.recoveryId, relativePath),
              ciphertextBytes: encryptedBytes.length,
              ciphertextSha512: sha512(encryptedBytes),
              plaintextBytes: chunk.length,
              plaintextSha512: envelope.plaintextSha512,
              mediaId: source.mediaId,
              contentType: PART_CONTENT_TYPE,
            },
            persisted: { relativePath, bytes: encryptedBytes },
          });
          globalIndex += 1;
        }
        if (oversized) break;
      }
      if (!oversized) return result;
      rawChunkBytes = Math.floor(rawChunkBytes * 0.75);
    }
    throw new Error('Nao foi possivel respeitar o limite multipart criptografado.');
  }

  private async encryptValue(value: unknown): Promise<EncryptedRecoveryExport> {
    const ephemeralSecretKey = await this.random.getBytes(nacl.box.secretKeyLength);
    const nonce = await this.random.getBytes(nacl.box.nonceLength);
    return encryptRecoveryJson(
      value,
      this.recipientPublicKey,
      ephemeralSecretKey,
      nonce,
      this.recipientKeyId
    ).encrypted;
  }

  private async assertPreparedObjects(
    prepared: PreparedRecoveryEscrow,
    input: RecoveryCaptureInput
  ): Promise<void> {
    const { manifest, manifestObject } = prepared;
    const expectedPrefix = `${objectPrefix(input.identity, input.recoveryId)}/`;
    const expectedLocalPrefix = localPartUri(input.identity, input.recoveryId, '');
    if (
      manifest.recoveryId !== input.recoveryId ||
      manifest.identity.companyId !== input.identity.companyId ||
      manifest.identity.userId !== input.identity.userId ||
      manifest.identity.deviceId !== input.identity.deviceId ||
      manifest.generatedAt !== input.createdAt ||
      manifest.partCount !== manifest.parts.length ||
      manifest.parts.some(part =>
        part.ciphertextBytes > input.maxPartBytes ||
        !part.objectPath.startsWith(expectedPrefix) ||
        !part.localUri.startsWith(expectedLocalPrefix)
      ) ||
      !manifestObject.objectPath.startsWith(expectedPrefix) ||
      !manifestObject.localUri.startsWith(expectedLocalPrefix)
    ) {
      throw new Error('Escrow local pertence a outra captura ou esta inconsistente.');
    }
    for (const part of prepared.manifest.parts) {
      await assertPersistedObject(this.store, part);
    }
    await assertPersistedObject(this.store, prepared.manifestObject);
  }
}

export const createAppMultipartRecoveryCaptureAdapter = (
  dependencies: AppRecoveryCaptureDependencies
): MultipartRecoveryCaptureAdapter => new MultipartRecoveryCaptureAdapter({
  ...dependencies,
  random: { getBytes: length => Random.getRandomBytesAsync(length) },
  recipientPublicKeyBase64: RECOVERY_DIAGNOSTICS_PUBLIC_KEY,
  recipientKeyId: RECOVERY_DIAGNOSTICS_PUBLIC_KEY_ID,
});

export const decryptRecoveryPartForVerification = (
  bytes: Uint8Array,
  recipientSecretKeyBase64: string
): RecoveryPlaintextPartEnvelope => {
  const encrypted = decodeJson<EncryptedRecoveryExport>(bytes);
  const envelope = decryptRecoveryJson(
    encrypted,
    recipientSecretKeyBase64
  ) as RecoveryPlaintextPartEnvelope;
  if (envelope.schema !== PART_SCHEMA) {
    throw new Error('Parte de recovery nao suportada.');
  }
  const payload = naclUtil.decodeBase64(envelope.payloadBase64);
  if (payload.length !== envelope.plaintextBytes || sha512(payload) !== envelope.plaintextSha512) {
    throw new Error('Parte de recovery corrompida.');
  }
  return envelope;
};
